import { SaveService } from '@core/services/SaveService';
import type { PlayerProfileData } from '@core/types';

const DEFAULT_PROFILE: PlayerProfileData = {
  name: '',
  coins: 0,
  highScores: {},
  unlocked: [],
  character: '',
};

/**
 * Fuente unica de verdad del estado del jugador. Cualquier escena o modulo lee y
 * escribe el progreso a traves de esta clase, que se encarga de persistirlo.
 * Se inyecta a las escenas mediante el registry global de Phaser.
 */
export class PlayerProfile {
  private data: PlayerProfileData;

  public constructor(private readonly saveService: SaveService) {
    const loaded = saveService.load();
    // Mezclamos con los valores por defecto para que perfiles guardados con versiones
    // anteriores reciban los campos nuevos (ej. character) sin romperse.
    this.data = loaded
      ? { ...structuredClone(DEFAULT_PROFILE), ...loaded }
      : structuredClone(DEFAULT_PROFILE);
  }

  public get name(): string {
    return this.data.name;
  }

  public get coins(): number {
    return this.data.coins;
  }

  public setName(name: string): void {
    this.data.name = name.trim();
    this.persist();
  }

  public get character(): string {
    return this.data.character;
  }

  public setCharacter(characterKey: string): void {
    this.data.character = characterKey;
    this.persist();
  }

  public addCoins(amount: number): void {
    if (amount <= 0) {
      return;
    }
    this.data.coins += amount;
    this.persist();
  }

  public getHighScore(minigameKey: string): number {
    return this.data.highScores[minigameKey] ?? 0;
  }

  /** Registra un puntaje y devuelve true si supero el record anterior. */
  public submitScore(minigameKey: string, score: number): boolean {
    const previous = this.getHighScore(minigameKey);
    if (score <= previous) {
      return false;
    }
    this.data.highScores[minigameKey] = score;
    this.persist();
    return true;
  }

  public isUnlocked(minigameKey: string): boolean {
    return this.data.unlocked.includes(minigameKey);
  }

  public unlock(minigameKey: string): void {
    if (this.isUnlocked(minigameKey)) {
      return;
    }
    this.data.unlocked.push(minigameKey);
    this.persist();
  }

  private persist(): void {
    this.saveService.save(this.data);
  }
}
